import React, { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import { useDragLayer } from 'react-dnd';
import styled from 'styled-components';
import { Page, Typo, Flex, Icon, SimpleButton, theme } from '../../../ui';
import PageSection from '../../../ui/Page/PageSection';
import DraggableRow from './DraggableRow';
import { VerticalDropZone } from './DropZone';
import Module from '../../Module';
import ModuleWrapper from './ModuleWrapper';
import ModuleInsertionZone from './ModuleInsertionZone';
import { CONTENT_STANDARD } from '../../../utils/media';
import { useCourseSectionById, useCoursePageById } from '../../viewCourse/hooks';
import { useCourseTheme } from '../../course/hooks';
import { getUserThemeColor, shadeColor } from '../../../ui/theme/colors';
import { Breadcrumbs } from '@mui/material';
import { useMoveModuleToNewRowMutation, useUpdatePageModuleMutation } from '../editCourseApi';
import { useSnackbar } from 'notistack';
import { useTranslation } from 'react-i18next';
import { TOAST_SUCCESS, TOAST_ERROR } from '../../../utils/constants';

const QuizHeaderWrapper = styled.div`
  background: ${({ $bgColor }) => $bgColor || theme.colors.NEU_50};
  border-radius: 16px;
  padding: 24px;
  width: 100%;
  margin-bottom: 16px;
`;

const IconWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
`;

const QuizInfo = styled(Flex)`
  flex: 1;
`;

const InfoItem = styled(Flex)`
  align-items: center;
`;

const InfoLabel = styled.span`
  color: ${theme.colors.NEU_600};
  font-size: 14px;
  margin-right: 6px;
`;

const InfoValue = styled.span`
  font-weight: 600;
  font-size: 14px;
`;

const QuestionNumber = styled.div`
  position: absolute;
  top: -10px;
  left: 50%;
  transform: translateX(-50%);
  background: ${({ $themeColor }) => $themeColor || theme.colors.NEU_600};
  color: ${theme.colors.NEU_0};
  font-size: 12px;
  font-weight: 600;
  padding: 2px 10px;
  border-radius: 10px;
  z-index: 5;
`;

const TaskWrapper = styled.div`
  position: relative;
  width: 100%;
`;

/**
 * QuizPageEditor - Enhanced page editor for quiz sections
 * Shows quiz header with icon, name, question count, and settings button
 * Tasks are numbered to show sequence
 *
 * @param {Object} props
 * @param {string} props.pageId - The page ID
 * @param {Object} props.pageInfo - Page data with pageRows
 * @param {Object} props.quizSettings - Quiz settings from course.settings.quiz.pages[pageId]
 * @param {Function} props.onEditModuleContent - Callback for editing module content
 * @param {Function} props.onEditModuleSettings - Callback for editing module settings (not used for tasks)
 * @param {Function} props.onDeleteModule - Callback for deleting a module
 * @param {Function} props.onInsertModule - Callback for inserting a new module
 * @param {Function} props.onOpenQuizSettings - Callback to open quiz settings panel
 */
const QuizPageEditor = ({ pageId, pageInfo, quizSettings, onEditModuleContent, onEditModuleSettings, onDeleteModule, onInsertModule, onOpenQuizSettings }) => {
  const { t } = useTranslation();
  const [isDragging, setIsDragging] = useState(false);
  const [draggedModule, setDraggedModule] = useState(null);
  const [activeVerticalDropZone, setActiveVerticalDropZone] = useState(null);

  const pageRows = pageInfo?.pageRows || [];

  // Count total tasks/questions
  const totalQuestions = useMemo(() => {
    return pageRows.reduce((count, row) => {
      const taskModules = row.pageModules?.filter((m) => m.type?.name === 'task') || [];
      return count + taskModules.length;
    }, 0);
  }, [pageRows]);

  // Track question numbers for each task module
  const questionNumbers = useMemo(() => {
    const numbers = {};
    let questionIndex = 1;
    pageRows.forEach((row) => {
      if (row.pageModules) {
        row.pageModules.forEach((module) => {
          if (module.type?.name === 'task') {
            numbers[module.id] = questionIndex;
            questionIndex++;
          }
        });
      }
    });
    return numbers;
  }, [pageRows]);

  // Index of the row that currently contains the dragged module (if any)
  const draggedRowIndex = useMemo(() => {
    if (!draggedModule) return null;
    const index = pageRows.findIndex((row) => row.pageModules && row.pageModules.some((m) => m.id === draggedModule.id));
    return index === -1 ? null : index;
  }, [draggedModule, pageRows]);

  const draggedRowModulesCount = useMemo(() => {
    if (draggedRowIndex === null) return null;
    const row = pageRows[draggedRowIndex];
    if (!row || !row.pageModules) return null;
    return row.pageModules.length;
  }, [draggedRowIndex, pageRows]);

  // Global drag-layer monitor
  const { isDragging: globalIsDragging } = useDragLayer((monitor) => ({
    isDragging: monitor.isDragging(),
  }));

  const prevGlobalIsDraggingRef = useRef(false);

  useEffect(() => {
    if (prevGlobalIsDraggingRef.current && !globalIsDragging && isDragging) {
      setIsDragging(false);
      setDraggedModule(null);
    }
    prevGlobalIsDraggingRef.current = globalIsDragging;
  }, [globalIsDragging, isDragging]);

  // Get theme color
  const courseTheme = useCourseTheme();
  const themeColor = useMemo(() => getUserThemeColor(courseTheme?.color), [courseTheme]);
  const headerBgColor = useMemo(() => shadeColor(themeColor, 0.92), [themeColor]);

  // RTK Query mutations
  const [moveModuleToNewRow] = useMoveModuleToNewRowMutation();
  const [updatePageModule] = useUpdatePageModuleMutation();
  const { enqueueSnackbar } = useSnackbar();

  // Get section and page info for breadcrumbs
  const pageFromViewCourseState = useCoursePageById(pageId);
  const activeSection = useCourseSectionById(pageFromViewCourseState?.sectionId);

  const breadcrumbs = useMemo(() => {
    return [
      <Typo.MediumText key={`section-${activeSection?.name}`}>{activeSection?.name}</Typo.MediumText>,
      <Typo.MediumText key={`page-${pageInfo?.name}`}>{pageInfo?.name}</Typo.MediumText>,
    ];
  }, [activeSection, pageInfo]);

  const handleDragStart = useCallback((module) => {
    setIsDragging(true);
    setDraggedModule(module);
  }, []);

  const handleDragEnd = useCallback(() => {
    setIsDragging(false);
    setDraggedModule(null);
  }, []);

  const handleDrop = useCallback(
    async (dropData) => {
      setIsDragging(false);
      setDraggedModule(null);

      // Quiz pages only support vertical drops (no horizontal/side-by-side placement)
      if (dropData.type === 'horizontal') {
        return;
      }

      try {
        if (dropData.type === 'vertical') {
          const pageRowPosition = dropData.targetPosition + 1;

          await moveModuleToNewRow({
            moduleId: dropData.moduleId,
            pageRowPosition: pageRowPosition,
            pageId: pageId,
          }).unwrap();

          enqueueSnackbar(t('globals.success'), { variant: TOAST_SUCCESS });
        } else if (dropData.type === 'horizontal') {
          const targetRow = pageRows.find((row) => row.id === dropData.targetRowId);
          if (!targetRow) {
            throw new Error('Target row not found');
          }

          const targetModules = [...targetRow.pageModules];
          const draggedModuleIndex = targetModules.findIndex((m) => m.id === dropData.moduleId);
          if (draggedModuleIndex !== -1) {
            targetModules.splice(draggedModuleIndex, 1);
          }

          let insertIndex;
          if (dropData.targetPosition === 'left') {
            insertIndex = 0;
          } else if (dropData.targetPosition === 'center') {
            insertIndex = 1;
          } else if (dropData.targetPosition === 'right') {
            insertIndex = targetModules.length;
          }

          const draggedModuleObj = draggedModuleIndex !== -1 ? targetRow.pageModules[draggedModuleIndex] : { id: dropData.moduleId };
          targetModules.splice(insertIndex, 0, draggedModuleObj);

          const updatePromises = targetModules.map((module, index) =>
            updatePageModule({
              moduleId: module.id,
              pageId: pageId,
              pageRowId: targetRow.id,
              sortOrder: index + 1,
            }).unwrap(),
          );

          await Promise.all(updatePromises);

          enqueueSnackbar(t('globals.success'), { variant: TOAST_SUCCESS });
        }
      } catch (error) {
        console.error('Failed to move module:', error);
        enqueueSnackbar(t('globals.error'), { variant: TOAST_ERROR });
      }
    },
    [pageId, pageRows, moveModuleToNewRow, updatePageModule, enqueueSnackbar, t],
  );

  const renderModule = useCallback(
    (module, props) => {
      const isTask = module.type?.name === 'task';
      const questionNumber = isTask ? questionNumbers[module.id] : null;

      return (
        <TaskWrapper key={props.key}>
          {isTask && questionNumber && !isDragging && (
            <QuestionNumber $themeColor={themeColor}>
              {t('quiz.question')} {questionNumber}
            </QuestionNumber>
          )}
          <ModuleWrapper
            module={module}
            isEditMode={true}
            pageInDragState={isDragging}
            isDraggedModule={!!draggedModule && draggedModule.id === module.id}
            onEditContent={onEditModuleContent}
            onEditSettings={isTask ? null : onEditModuleSettings} // No settings for tasks
            onDelete={onDeleteModule}
            onDragStart={handleDragStart}
            onDragEnd={handleDragEnd}
            enableDrag={true}
            themeColor={themeColor}
          >
            <Module module={module} columns={props.$columns} themeColor={themeColor} isQuiz={true} />
          </ModuleWrapper>
        </TaskWrapper>
      );
    },
    [isDragging, draggedModule, themeColor, questionNumbers, onEditModuleContent, onEditModuleSettings, onDeleteModule, handleDragStart, handleDragEnd, t],
  );

  return (
    <Page>
      <PageSection align='center' gap='20' size={CONTENT_STANDARD}>
      {/* Breadcrumbs */}
      <Breadcrumbs separator='›' aria-label='breadcrumb'>
        {breadcrumbs}
      </Breadcrumbs>

      {/* Quiz Header */}
      <QuizHeaderWrapper $bgColor={headerBgColor}>
        {/* <QuizHeader gap='20'> */}
        <Flex direction='column' align='center' gap='8'>
          <IconWrapper>
            <Icon.Questions size='xxl' color={themeColor} />
          </IconWrapper>
          <QuizInfo align='center' gap='24'>
            <InfoItem>
              <InfoLabel>{t('quiz.quiz')}:</InfoLabel>
              <InfoValue>{pageInfo?.name || t('quiz.quiz')}</InfoValue>
            </InfoItem>
            <InfoItem>
              <InfoLabel>{t('quiz.questions')}:</InfoLabel>
              <InfoValue>{totalQuestions}</InfoValue>
            </InfoItem>
            <SimpleButton icon='Settings' onClick={onOpenQuizSettings} variant='ghost' size='small' tooltip={t('quiz.settings')} />
          </QuizInfo>
        </Flex>
        {/* </QuizHeader> */}
      </QuizHeaderWrapper>

      {/* Top drop zone */}
      {isDragging && (
        <VerticalDropZone
          position={0}
          onDrop={handleDrop}
          isVisible={activeVerticalDropZone === 'top'}
          themeColor={themeColor}
          onHoverChange={(isHovered) => {
            if (isHovered) {
              if (draggedRowIndex === 0 && draggedRowModulesCount === 1) {
                return;
              }
              setActiveVerticalDropZone('top');
            } else if (activeVerticalDropZone === 'top') {
              setActiveVerticalDropZone(null);
            }
          }}
        />
      )}

      {/* Empty page state */}
      {pageRows.length === 0 && onInsertModule && (
        <ModuleInsertionZone position={0} onInsertModule={onInsertModule} isEmpty={true} defaultModuleType='task' contextType='quiz' />
      )}

      {pageRows.map((pageRow, index) => {
        const isDraggedModuleRow = draggedModule && pageRow.pageModules.some((m) => m.id === draggedModule.id);
        const isLastRow = index === pageRows.length - 1;
        const isFirstRow = index === 0;
        const draggedModuleIsLast = draggedModule && isLastRow && isDraggedModuleRow;

        return (
          <React.Fragment key={`pageRow-${pageRow.id}`}>
            <DraggableRow
              pageRow={pageRow}
              isNotesOpen={false}
              renderModule={renderModule}
              isDragging={isDragging}
              draggedModule={draggedModule}
              onDrop={handleDrop}
              themeColor={themeColor}
              verticalOnly={true}
              onTopHalfHover={(isHovered) => {
                if (!isHovered) return;

                if (draggedRowIndex !== null && draggedRowModulesCount === 1) {
                  if (isFirstRow) {
                    if (draggedRowIndex === 0) return;
                  } else {
                    const verticalIndex = index - 1;
                    if (verticalIndex === draggedRowIndex - 1 || verticalIndex === draggedRowIndex) return;
                  }
                }

                setActiveVerticalDropZone(isFirstRow ? 'top' : index - 1);
              }}
              onBottomHalfHover={(isHovered) => {
                if (!isHovered) return;

                if (draggedRowIndex !== null && draggedRowModulesCount === 1) {
                  const verticalIndex = index;
                  if (verticalIndex === draggedRowIndex - 1 || verticalIndex === draggedRowIndex) return;
                }

                if (isLastRow && draggedModuleIsLast) return;

                setActiveVerticalDropZone(index);
              }}
            />

            {/* Vertical drop zone after each row (except last row) */}
            {!isLastRow && (
              <VerticalDropZone
                position={index + 1}
                onDrop={handleDrop}
                isVisible={isDragging && activeVerticalDropZone === index}
                themeColor={themeColor}
                onHoverChange={(isHovered) => {
                  if (isHovered) {
                    if (draggedRowIndex !== null && draggedRowModulesCount === 1) {
                      const verticalIndex = index;
                      if (verticalIndex === draggedRowIndex - 1 || verticalIndex === draggedRowIndex) return;
                    }

                    if (isLastRow && draggedModuleIsLast) return;
                    setActiveVerticalDropZone(index);
                  } else if (activeVerticalDropZone === index) {
                    setActiveVerticalDropZone(null);
                  }
                }}
              />
            )}

            {/* Insertion zone for adding new modules - only task allowed in quiz */}
            {onInsertModule && !isDragging && (
              <ModuleInsertionZone position={index + 1} onInsertModule={onInsertModule} isEmpty={false} defaultModuleType='task' />
            )}
          </React.Fragment>
        );
      })}
      </PageSection>
    </Page>
  );
};

export default QuizPageEditor;
